import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { storageService } from '../../../services/storageService';

const HeroImageUpload = ({ heroImage, heroImageAlt, onImageUpload, onImageRemove, postId }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFileSize, setUploadedFileSize] = useState(0);
  const [imageStatus, setImageStatus] = useState('loading');
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileUpload(files?.[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e?.target?.files;
    if (files?.length > 0) {
      handleFileUpload(files?.[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file?.type?.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file?.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      // Generate temporary post ID if creating new post
      const tempPostId = postId || `temp-${Date.now()}`;
      
      console.log('[HeroUpload] Starting upload with postId:', tempPostId);
      setUploadProgress(30);
      
      const result = await storageService?.uploadHeroImage(tempPostId, file);
      
      console.log('[HeroUpload] Upload result:', result);
      console.log('[HeroUpload] Generated publicUrl:', result?.url);
      setUploadProgress(90);

      // Validate URL before proceeding
      if (!result?.url) {
        console.error('[HeroUpload] Upload succeeded but URL is missing:', result);
        throw new Error('Upload succeeded but failed to generate image URL');
      }

      console.log('[HeroUpload] Setting image with URL:', result?.url);
      console.log('[HeroUpload] Preview src used:', result?.url);

      // Store file metadata for display
      setUploadedFileName(file?.name);
      setUploadedFileSize(file?.size);

      // Reset image status for new image
      setImageStatus('loading');

      // Immediately update with STRING URL
      onImageUpload(result?.url, heroImageAlt);
      
      setIsUploading(false);
      setUploadProgress(0);
      console.log('[HeroUpload] Upload complete and preview updated');
    } catch (error) {
      const errorMessage = error?.message || 'Upload failed';
      console.error('[HeroUpload] Upload error:', error);
      setUploadError(errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageLoad = () => {
    console.log('[HeroUpload] Image loaded successfully');
    setImageStatus('loaded');
  };

  const handleImageError = (e) => {
    console.error('[HeroUpload] Image failed to load:', e);
    console.error('[HeroUpload] Error target:', e?.target);
    console.error('[HeroUpload] Error message:', e?.message || 'Unknown error');
    setImageStatus('error');
  };

  // Debug log as requested
  console.log('HERO IMAGE URL:', heroImage);

  // CRITICAL: Only render preview if heroImage exists, otherwise render placeholder
  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-semibold text-foreground">Hero Image</h3>
        {heroImage && (
          <Button
            variant="ghost"
            size="sm"
            iconName="Trash2"
            iconPosition="left"
            onClick={onImageRemove}
          >
            Remove
          </Button>
        )}
      </div>
      {heroImage ? (
        <div className="space-y-4">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted border-2 border-blue-500">
            <img
  src={heroImage}
  alt="Hero image"
  className="w-full h-full object-cover"
  style={{ width: '100%', height: 'auto', display: 'block' }}
  onLoad={() => {
  setImageStatus('loaded');
}}
  onError={() => {
  setImageStatus('error');
}}

/>
          </div>
          
          

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Alt Text</label>
            <input
              type="text"
              value={heroImageAlt || ''}
              onChange={(e) => onImageUpload(heroImage, e?.target?.value)}
              placeholder="Describe the image for accessibility"
              className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {uploadedFileName && (
              <p className="text-xs text-muted-foreground">
                {uploadedFileName} • {(uploadedFileSize / 1024)?.toFixed(2)} KB
              </p>
            )}
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition-all duration-250 ${
            isDragging
              ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          {isUploading ? (
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Upload" className="w-8 h-8 text-primary animate-pulse" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Uploading...</p>
                <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
              </div>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="Image" className="w-8 h-8 text-muted-foreground" />
              </div>
              <h4 className="text-base font-semibold text-foreground mb-2">
                Drop your hero image here
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                or click to browse from your computer
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef?.current?.click()}
              >
                Choose File
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Recommended: 1200x630px • Max 5MB • JPG, PNG, WebP
              </p>
            </>
          )}

          {uploadError && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{uploadError}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeroImageUpload;